import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_URL } from '../../lib/api-client';
import { logger } from '../../utils/logger';

interface Plan {
  _id: string;
  name: string;
  slug: string;
  description: string;
  price: number;
  duration: number;
  finalPrice: number;
  discount: number;
  isPopular: boolean;
  features: any;
  customFeatures?: string[];
}

interface Subscription {
  planId: any;
  planName: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
  features: any;
}

interface Order {
  _id: string;
  planId: any;
  originalAmount: number;
  discountAmount: number;
  finalAmount: number;
  paymentStatus: string;
  status: string;
  createdAt: string;
  subscription: {
    startDate: string;
    endDate: string;
  };
  couponId?: any;
}

interface PlanState {
  plans: Plan[];
  currentSubscription: Subscription | null;
  paymentHistory: Order[];
  loading: boolean;
  error: string | null;
  lastFetched: number | null;
  cacheValid: boolean;
}

const initialState: PlanState = {
  plans: [],
  currentSubscription: null,
  paymentHistory: [],
  loading: false,
  error: null,
  lastFetched: null,
  cacheValid: false,
};

// Cache duration: 5 minutes
const CACHE_DURATION = 5 * 60 * 1000;

// Fetch all plans
export const fetchPlans = createAsyncThunk(
  'plan/fetchPlans',
  async (forceRefresh: boolean = false, { getState, rejectWithValue }) => {
    try {
      const state = getState() as any;
      const planState = state.plan;

      // Check if cache is still valid
      if (
        !forceRefresh &&
        planState.cacheValid &&
        planState.lastFetched &&
        Date.now() - planState.lastFetched < CACHE_DURATION &&
        planState.plans.length > 0
      ) {
        logger.log('✅ Using cached plans data');
        return null; // Return null to indicate cached data is being used
      }

      logger.log('🔄 Fetching fresh plans data from API');
      const token = localStorage.getItem('token');
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }

      const response = await axios.get(`${API_URL}/plans`);
      return response.data.data.plans;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch plans');
    }
  }
);

// Fetch current subscription
export const fetchCurrentSubscription = createAsyncThunk(
  'plan/fetchCurrentSubscription',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return null;
      }

      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await axios.get(`${API_URL}/user/subscription`);
      
      logger.log('📡 [fetchCurrentSubscription] API Response:', response.data);
      
      // Extract the subscription object from the response
      const responseData = response.data.data;
      if (responseData?.hasSubscription && responseData?.subscription) {
        logger.log('✅ [fetchCurrentSubscription] Subscription found:', responseData.subscription);
        return responseData.subscription;
      }
      logger.log('⚠️ [fetchCurrentSubscription] No active subscription');
      return null;
    } catch (error: any) {
      // No subscription is not an error
      if (error.response?.status === 404) {
        return null;
      }
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch subscription');
    }
  }
);

// Fetch payment history
export const fetchPaymentHistory = createAsyncThunk(
  'plan/fetchPaymentHistory',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return [];
      }

      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await axios.get(`${API_URL}/payments/orders`);
      return response.data.data.orders || [];
    } catch (error: any) {
      // No payment history is not an error
      if (error.response?.status === 404) {
        return [];
      }
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch payment history');
    }
  }
);

// Create order
export const createOrder = createAsyncThunk(
  'plan/createOrder',
  async (orderData: { planId: string; couponCode?: string }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated');
      }

      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await axios.post(`${API_URL}/payments/create-order`, orderData);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create order');
    }
  }
);

// Verify payment
export const verifyPayment = createAsyncThunk(
  'plan/verifyPayment',
  async (paymentData: {
    razorpayOrderId: string;
    razorpayPaymentId: string;
    razorpaySignature: string;
  }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated');
      }

      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await axios.post(`${API_URL}/payments/verify`, paymentData);
      return response.data.data.order;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to verify payment');
    }
  }
);

// Validate coupon
export const validateCoupon = createAsyncThunk(
  'plan/validateCoupon',
  async (couponData: { code: string; planId: string }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }

      const response = await axios.post(`${API_URL}/coupons/validate`, couponData);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Invalid coupon');
    }
  }
);

const planSlice = createSlice({
  name: 'plan',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    updateSubscription: (state, action) => {
      state.currentSubscription = action.payload;
    },
    addPaymentToHistory: (state, action) => {
      state.paymentHistory.unshift(action.payload);
    }
  },
  extraReducers: (builder) => {
    // Fetch plans
    builder
      .addCase(fetchPlans.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPlans.fulfilled, (state, action) => {
        if (action.payload) {
          state.loading = false;
          state.plans = action.payload;
          state.lastFetched = Date.now();
          state.cacheValid = true;
        } else {
          logger.log('🔄 Using cached plans data');
        }
      })
      .addCase(fetchPlans.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch current subscription
    builder
      .addCase(fetchCurrentSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCurrentSubscription.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSubscription = action.payload;
        logger.log('✅ [planSlice] Subscription updated in Redux:', action.payload);
      })
      .addCase(fetchCurrentSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch payment history
    builder
      .addCase(fetchPaymentHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPaymentHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.paymentHistory = action.payload;
      })
      .addCase(fetchPaymentHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Verify payment
    builder
      .addCase(verifyPayment.fulfilled, (state, action) => {
        // Add the completed order to payment history
        state.paymentHistory.unshift(action.payload);
      });
  }
});

export const { clearError, updateSubscription, addPaymentToHistory } = planSlice.actions;

export default planSlice.reducer;